/*
 * The 8 VNG (Vereniging van Nederlandse Gemeenten) process steps
 * for responsible algorithm and AI governance inside Dutch municipalities.
 *
 * Narratives written in Dutch — the primary language of the VNG framework —
 * with a short English subtitle for international auditors.
 */

import {
  Compass,
  Database,
  PenLine,
  Cog,
  ShieldCheck,
  Rocket,
  Activity,
  BookOpenCheck,
} from "lucide-react";

export const vngSteps = [
  {
    id: "initiatie",
    number: "01",
    title: "Initiatie",
    subtitle: "Initiation",
    icon: Compass,
    legal: { article: "Art. 9", topic: "Risicomanagement" },
    summary:
      "Vaststellen van het maatschappelijk doel, de wettelijke grondslag en de betrokken stakeholders voordat een algoritme wordt ontwikkeld.",
    narrative:
      "In de initiatiefase wordt het probleem scherp geformuleerd. Waarom is een algoritmische oplossing nodig, welk publiek belang dient zij, en welke juridische basis (AVG, AWB, gemeentewet) rechtvaardigt de verwerking? RiskaiΛ legt automatisch een auditspoor aan: probleemstelling, doelbinding, stakeholders en initiële risico-indicatie.",
    placeholder:
      "Dit assessment betreft het 'Algorithmic Fraud Detection Framework' van Gemeente Leeuwarden. De juridische grondslag berust op artikel 6 lid 1 onder e AVG (taak van algemeen belang) in samenhang met de Gemeentewet en het Burgerservicenummerbesluit. Stakeholders zijn vastgelegd in het stakeholderregister; het formele go-besluit is genomen door het college van Burgemeester & Wethouders d.d. 12-03-2026.",
    artifacts: ["Probleemstelling", "Doelbinding", "Stakeholder-register", "Go / No-go"],
  },
  {
    id: "data-analyse",
    number: "02",
    title: "Data-analyse",
    subtitle: "Data analysis",
    icon: Database,
    legal: { article: "Art. 10", topic: "Datagovernance" },
    summary:
      "Inventariseren van databronnen, datakwaliteit, bias en bijzondere persoonsgegevens volgens de VNG-datascan.",
    narrative:
      "Elke dataset wordt gekarakteriseerd: herkomst, rechtmatigheid, actualiteit, representativiteit en risico op historische bias. RiskaiΛ voert continue drift- en kwaliteitscontroles uit en signaleert wanneer trainingsdata afwijkt van productiedata — een kritieke vereiste onder de AI-Verordening voor hoog-risico systemen.",
    placeholder:
      "Voor de uitvoering van de risico-analyse zijn alle databronnen geïnventariseerd conform de VNG-datascan. De trainingsdata is onderworpen aan een bias-onderzoek op de demografische assen leeftijd, geslacht, wijk en migratie-achtergrond. Kwaliteit, actualiteit en proportionaliteit van iedere bron zijn vastgelegd in het databron-register; bijzondere persoonsgegevens zijn afzonderlijk gemarkeerd met de bijbehorende artikel-9 AVG grondslag.",
    artifacts: ["Databron-inventaris", "DPIA-input", "Bias-rapport", "Kwaliteitsscore"],
  },
  {
    id: "ontwerp",
    number: "03",
    title: "Ontwerp",
    subtitle: "Design",
    icon: PenLine,
    legal: { article: "Art. 11", topic: "Technische documentatie" },
    summary:
      "Functioneel en ethisch ontwerp van het algoritme, inclusief mensenrechten­toets (IAMA) en uitlegbaarheidseisen.",
    narrative:
      "Hier worden architectuurkeuzes vastgelegd: welk modeltype, welke features, welke beslisdrempels. Parallel draait de Impact Assessment Mensenrechten & Algoritmes (IAMA). Ontwerpkeuzes worden gekoppeld aan mitigerende maatregelen zodat de gemeente bij elk besluit kan verantwoorden waarom die keuze is gemaakt.",
    placeholder:
      "Het functioneel en ethisch ontwerp is vastgelegd in architectuurnota v1.2 inclusief feature-definitie, beslisdrempels en uitlegbaarheidsplan. De Impact Assessment Mensenrechten & Algoritmes (IAMA) is uitgevoerd door de Functionaris Gegevensbescherming en getoetst aan het toetsingskader Algoritmes van de Auditdienst Rijk; advies is positief mits de zeven mitigerende maatregelen in bijlage B onverkort worden geïmplementeerd.",
    artifacts: ["IAMA", "Architectuurnota", "Feature-definitie", "Uitlegbaarheidsplan"],
  },
  {
    id: "ontwikkeling",
    number: "04",
    title: "Ontwikkeling",
    subtitle: "Development",
    icon: Cog,
    legal: { article: "Art. 13", topic: "Transparantie" },
    summary:
      "Gecontroleerd bouwen van het model met versiebeheer, reproduceer­baarheid en four-eyes review.",
    narrative:
      "Modellen worden in een afgeschermde omgeving getraind. Iedere training wordt vastgelegd met seed, hyperparameters, data-snapshot en evaluatiemetriek. RiskaiΛ bewaart het volledige lineage zodat een extern toezichthouder (AP, ADR) elk model exact kan reconstrueren.",
    placeholder:
      "De modelontwikkeling heeft plaatsgevonden in een afgeschermde testomgeving met versiebeheer in een gecontroleerde Git-repository, gescheiden van productie. Iedere trainingsrun is voorzien van een vaste seed, gedocumenteerde hyperparameters en een data-snapshot met cryptografische hash. Een onafhankelijke four-eyes code-review is uitgevoerd door [reviewer] en akkoord bevonden; het volledige model-lineage is reproduceerbaar voor inspectie door AP of ADR.",
    artifacts: ["Model-lineage", "Code-review", "Testomgeving", "Trainingsprotocol"],
  },
  {
    id: "validatie",
    number: "05",
    title: "Validatie",
    subtitle: "Validation",
    icon: ShieldCheck,
    legal: { article: "Art. 14", topic: "Menselijk toezicht" },
    summary:
      "Onafhankelijke toetsing op performance, eerlijkheid, robuustheid en juridische naleving vóór livegang.",
    narrative:
      "Een onafhankelijke validator toetst het model tegen drempelwaarden voor nauwkeurigheid, false-positive rate per demografische groep, robuustheid tegen adversarial input en naleving van de AI-Verordening. Zonder getekend validatierapport kan een algoritme in RiskaiΛ niet promoveren naar productie.",
    placeholder:
      "De onafhankelijke validatie is uitgevoerd door [naam validator], met toetsing op nauwkeurigheid (F1-score ≥ 0.92), fairness per demografische groep (Δ false-positive-rate < 5%), robuustheid tegen adversarial input en juridische naleving van de AI-Verordening voor hoog-risico systemen. Het ondertekende validatierapport d.d. [datum] is bijgevoegd als bewijsstuk. Het systeem voldoet aan alle drempelwaarden en is geautoriseerd voor productie.",
    artifacts: ["Validatierapport", "Fairness-metriek", "Robuustheidstest", "Sign-off"],
  },
  {
    id: "implementatie",
    number: "06",
    title: "Implementatie",
    subtitle: "Deployment",
    icon: Rocket,
    legal: { article: "Art. 15", topic: "Nauwkeurigheid & robuustheid" },
    summary:
      "Gecontroleerde uitrol in productie met menselijke tussenkomst, registratie in het Algoritmeregister en burgercommunicatie.",
    narrative:
      "Bij livegang wordt het algoritme automatisch gepubliceerd in het landelijke Algoritmeregister. Er worden menselijke checkpoints (human-in-the-loop) gedefinieerd, bezwaarroutes ingericht en burgers geïnformeerd in begrijpelijke taal. RiskaiΛ bewaakt dat geen enkel hoog-risico besluit volautomatisch wordt genomen.",
    placeholder:
      "Het algoritme is bij livegang automatisch gepubliceerd in het landelijke Algoritmeregister onder nummer [AR-nr]. Menselijke checkpoints zijn ingericht conform het human-in-the-loop principe: geen enkel hoog-risico besluit wordt volautomatisch genomen. De bezwaarroute volgt de AWB; burgers worden actief geïnformeerd in B1-niveau Nederlands via de gemeentelijke website en de jaarlijkse bewonersbrief.",
    artifacts: ["Algoritmeregister", "HITL-configuratie", "Bezwaarroute", "Burgercommunicatie"],
  },
  {
    id: "monitoring",
    number: "07",
    title: "Monitoring",
    subtitle: "Continuous monitoring",
    icon: Activity,
    legal: { article: "Art. 61", topic: "Monitoring na ingebruikname" },
    summary:
      "Continue bewaking van performance, drift, eerlijkheid en incidenten — het hart van Continu Gecertificeerd AI.",
    narrative:
      "Modellen verouderen zodra de werkelijkheid verandert. RiskaiΛ draait 24/7 monitoring op data-drift, fairness-drift en incidenten. Wanneer een KPI een drempel overschrijdt wordt automatisch een alert, een human review en — indien nodig — een kill-switch in werking gezet.",
    placeholder:
      "Continue monitoring is actief sinds [datum livegang] en bewaakt 24/7 de KPI's data-drift, fairness-drift, modelprestatie en incidentmeldingen. Drempelwaarden zijn vooraf vastgelegd in het monitoringsplan. Bij overschrijding wordt automatisch een alert verzonden naar de verantwoordelijk portefeuillehouder, een human review getriggerd en, indien noodzakelijk, de kill-switch in werking gezet — conform artikel 61 AI-Verordening.",
    artifacts: ["Drift-dashboard", "Incident-log", "Alerting", "Kill-switch"],
  },
  {
    id: "evaluatie",
    number: "08",
    title: "Evaluatie",
    subtitle: "Evaluation & accountability",
    icon: BookOpenCheck,
    legal: { article: "Art. 62", topic: "Incidentrapportage" },
    summary:
      "Periodieke heroverweging: werkt het algoritme nog, is het nog proportioneel, en wat leren we voor de volgende cyclus?",
    narrative:
      "Minimaal jaarlijks — en bij elke materiële wijziging — vindt een formele evaluatie plaats. RiskaiΛ levert een kant-en-klaar jaarverslag voor college, gemeenteraad en toezichthouder, en voedt de initiatiefase van het volgende algoritme met geleerde lessen. Zo sluit de lifecycle.",
    placeholder:
      "De periodieke evaluatie heeft plaatsgevonden conform de jaarlijkse cyclus, met aanvullende toetsing bij elke materiële wijziging. De resultaten zijn opgenomen in de jaarrapportage aan college van B&W en gemeenteraad en aangeboden aan de toezichthouder. Lessons learned zijn teruggevoerd naar de initiatiefase van het volgende algoritme; proportionaliteit en blijvende noodzaak van het systeem zijn opnieuw bevestigd.",
    artifacts: ["Jaarrapportage", "Raad-brief", "Lessons learned", "Herijking"],
  },
];
